import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import Table from '../components/Table';
import Pagination from '../components/Pagination';
import StatsCard from '../components/StatsCard';
import toast from 'react-hot-toast';
import {
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ChartBarIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';
import { STEP_LABELS, getSiteTypeLabel } from '../utils/wizardV2Progress';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const stepBadgeClasses = {
  'initial-prompt': 'bg-gray-100 text-gray-700',
  'website-type': 'bg-sky-100 text-sky-800',
  'business-name': 'bg-indigo-100 text-indigo-800',
  audience: 'bg-purple-100 text-purple-800',
  design: 'bg-pink-100 text-pink-800',
  logo: 'bg-amber-100 text-amber-800',
  seo: 'bg-emerald-100 text-emerald-800',
  completed: 'bg-green-100 text-green-800',
};

const statusBadgeClasses = {
  active: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  expired: 'bg-red-100 text-red-800',
};

const SITE_TYPE_OPTIONS = [
  { value: '', label: 'All site types' },
  { value: 'landing_page', label: 'Landing page' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'catalog', label: 'Catalog' },
  { value: 'ecommerce', label: 'E-commerce' },
];

const formatLanguage = (lang) => {
  if (!lang) return '—';
  return lang.toString().toUpperCase();
};

const WizardSessions = () => {
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [versionFilter, setVersionFilter] = useState('v2');
  const [statusFilter, setStatusFilter] = useState('');
  const [siteTypeFilter, setSiteTypeFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20,
        sort_by: sortBy,
        sort_order: sortOrder,
        ...(versionFilter && versionFilter !== 'all' && { version: versionFilter }),
        ...(statusFilter && { status: statusFilter }),
        ...(siteTypeFilter && { site_type: siteTypeFilter }),
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
      });
      const response = await axios.get(`${API_BASE_URL}/api/admin/wizard-sessions?${params}`);
      setSessions(response.data.data.sessions || []);
      setTotalPages(response.data.data.pagination.totalPages);
    } catch (error) {
      console.error('Error fetching wizard sessions:', error);
      toast.error('Failed to fetch wizard sessions');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/wizard-sessions/stats/overview`);
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching wizard session stats:', error);
    }
  };

  useEffect(() => {
    fetchSessions();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, versionFilter, statusFilter, siteTypeFilter, debouncedSearchTerm, sortBy, sortOrder]);

  const handleSort = (field, order) => {
    setSortBy(field);
    setSortOrder(order);
    setCurrentPage(1);
  };

  const cleanupExpired = async () => {
    if (!window.confirm('Are you sure you want to delete all expired sessions?')) return;
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/wizard-sessions/cleanup/expired`
      );
      toast.success(response.data.message);
      fetchSessions();
      fetchStats();
    } catch (error) {
      console.error('Error cleaning up expired sessions:', error);
      toast.error('Failed to cleanup expired sessions');
    }
  };

  const clearFilters = () => {
    setVersionFilter('v2');
    setStatusFilter('');
    setSiteTypeFilter('');
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setCurrentPage(1);
  };

  const hasActiveFilters =
    versionFilter !== 'v2' || statusFilter || siteTypeFilter || searchTerm;

  const topDropoff = useMemo(() => {
    if (!stats?.dropoffByV2Step?.length) return null;
    return stats.dropoffByV2Step
      .filter((d) => d.count > 0)
      .sort((a, b) => b.count - a.count)[0];
  }, [stats]);

  const v2CompletionRate = stats?.completionRateByVersion?.v2 ?? 0;

  const columns = [
    {
      key: 'business_name',
      label: 'Session',
      sortable: false,
      render: (row) => (
        <div className="min-w-0">
          <div className="font-medium text-gray-900 truncate max-w-md">
            {row.business_name || row.initial_prompt_excerpt || '(untitled)'}
          </div>
          {row.business_name && row.initial_prompt_excerpt && (
            <div className="text-xs text-gray-500 truncate max-w-md mt-0.5">
              {row.initial_prompt_excerpt}
            </div>
          )}
          <div className="text-[11px] font-mono text-gray-400 mt-0.5">
            {row.session_id?.substring(0, 12)}...
          </div>
        </div>
      ),
    },
    {
      key: 'version',
      label: 'Version',
      sortable: false,
      render: (row) =>
        row.version === 'v2' ? (
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-primary-100 text-primary-800">
            v2
          </span>
        ) : (
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">
            Legacy v1
          </span>
        ),
    },
    {
      key: 'site_type',
      label: 'Site type',
      sortable: false,
      render: (row) =>
        row.site_type ? (
          <span className="px-2 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-700">
            {getSiteTypeLabel(row.site_type)}
          </span>
        ) : (
          <span className="text-gray-300">—</span>
        ),
    },
    {
      key: 'language',
      label: 'Lang',
      sortable: false,
      render: (row) => (
        <span className="text-xs font-mono text-gray-700">{formatLanguage(row.language)}</span>
      ),
    },
    {
      key: 'current_step',
      label: 'Step',
      sortable: false,
      render: (row) => {
        if (row.version !== 'v2' || !row.current_step) {
          return <span className="text-gray-300">—</span>;
        }
        const cls = stepBadgeClasses[row.current_step] || 'bg-gray-100 text-gray-700';
        return (
          <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${cls}`}>
            {STEP_LABELS[row.current_step] || row.current_step}
          </span>
        );
      },
    },
    {
      key: 'steps_completed',
      label: 'Progress',
      sortable: false,
      render: (row) => {
        if (row.version !== 'v2') {
          return <span className="text-gray-300 text-xs">—</span>;
        }
        const total = 7;
        const pct = Math.min(100, Math.round((row.steps_completed / total) * 100));
        return (
          <div className="w-24">
            <div className="text-[11px] text-gray-600 mb-0.5">
              {row.steps_completed}/{total}
            </div>
            <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500 rounded-full"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      sortable: false,
      render: (row) => {
        const cls = statusBadgeClasses[row.status] || 'bg-gray-100 text-gray-700';
        const label =
          row.status === 'active'
            ? 'Active'
            : row.status === 'completed'
            ? 'Completed'
            : row.status === 'expired'
            ? 'Expired'
            : row.status;
        return (
          <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${cls}`}>{label}</span>
        );
      },
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (row) => (
        <span className="text-xs text-gray-700">
          {new Date(row.created_at).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (row) =>
        row.version === 'v2' ? (
          <a
            href={`/wizard-sessions/${row.session_id}`}
            className="text-primary-600 hover:text-primary-900 text-sm font-medium"
          >
            View Details
          </a>
        ) : (
          <span
            className="text-gray-400 text-sm cursor-not-allowed"
            title="Legacy v1 session — no detail view"
          >
            Legacy
          </span>
        ),
    },
  ];

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Wizard Sessions</h1>
            <p className="text-sm text-gray-500 mt-1">
              v2 card-based wizard funnel. Legacy v1 sessions still show as thin rows.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={cleanupExpired} className="btn-danger">
              Cleanup Expired
            </button>
            <button
              onClick={() => {
                fetchSessions();
                fetchStats();
              }}
              disabled={loading}
              className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh data"
            >
              <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* KPI strip */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            <StatsCard
              title="Total sessions"
              value={stats.total}
              icon={DocumentTextIcon}
              trendValue={`v2: ${stats.byVersion?.v2 ?? 0} · v1: ${stats.byVersion?.v1 ?? 0}`}
              compact
            />
            <StatsCard
              title="Active v2"
              value={stats.activeV2Total ?? 0}
              icon={RocketLaunchIcon}
              trendValue={`${stats.active} active total`}
              compact
            />
            <StatsCard
              title="Completed v2"
              value={
                stats.byVersion?.v2 > 0
                  ? Math.round((v2CompletionRate / 100) * stats.byVersion.v2)
                  : 0
              }
              icon={CheckCircleIcon}
              trendValue={`${v2CompletionRate}% completion`}
              compact
            />
            <StatsCard
              title="Avg completion (v2)"
              value={`${parseFloat(stats.avgCompletionTimeMinutes).toFixed(1)}m`}
              icon={ClockIcon}
              trendValue={`${stats.expired} expired`}
              compact
            />
            <StatsCard
              title="Top drop-off step"
              value={
                topDropoff
                  ? STEP_LABELS[topDropoff.step] || topDropoff.step
                  : '—'
              }
              icon={ChartBarIcon}
              trendValue={
                topDropoff
                  ? `${topDropoff.count} stuck · ${topDropoff.percentage}%`
                  : 'No active v2 sessions'
              }
              compact
            />
          </div>
        )}

        {/* Filter bar */}
        <div className="bg-white rounded-lg shadow p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="lg:col-span-2 relative">
              <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search prompt, business name, or session id"
                className="input-field w-full pl-9 pr-9"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              )}
            </div>
            <select
              value={versionFilter}
              onChange={(e) => {
                setVersionFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="input-field"
            >
              <option value="all">All versions</option>
              <option value="v2">v2 only</option>
              <option value="v1">Legacy v1 only</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="input-field"
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="expired">Expired</option>
            </select>
            <select
              value={siteTypeFilter}
              onChange={(e) => {
                setSiteTypeFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="input-field"
            >
              {SITE_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          {hasActiveFilters && (
            <div className="mt-3 flex items-center justify-between text-xs">
              <span className="text-gray-500">Filters applied</span>
              <button
                onClick={clearFilters}
                className="text-primary-600 hover:text-primary-800 font-medium"
              >
                Reset filters
              </button>
            </div>
          )}
        </div>

        {/* Drop-off bar */}
        {stats?.dropoffByV2Step?.length > 0 && stats.activeV2Total > 0 && (
          <div className="bg-white rounded-lg shadow p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-700">Active v2 sessions by step</h3>
              <span className="text-xs text-gray-500">{stats.activeV2Total} active</span>
            </div>
            <div className="grid grid-cols-7 gap-2">
              {stats.dropoffByV2Step.map((bucket) => (
                <div key={bucket.step} className="text-center">
                  <div className="h-16 bg-gray-100 rounded relative overflow-hidden flex items-end">
                    <div
                      className="w-full bg-primary-500 transition-all"
                      style={{ height: `${Math.max(2, bucket.percentage)}%` }}
                      title={`${bucket.count} sessions (${bucket.percentage}%)`}
                    />
                  </div>
                  <div className="mt-1 text-[11px] font-medium text-gray-700 truncate">
                    {STEP_LABELS[bucket.step] || bucket.step}
                  </div>
                  <div className="text-[10px] text-gray-500">
                    {bucket.count} · {bucket.percentage}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <Table
            columns={columns}
            data={sessions}
            loading={loading}
            emptyMessage="No wizard sessions match the current filters"
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default WizardSessions;
